import {messages} from '@smartmixin/i18n';
import {
  CaretRight,
  EnvelopeSimple,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import {
  alpha,
  Box,
  Button,
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import ReactGA from 'react-ga4';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router';
import {
  CATEGORY_MESSAGES,
  HELP_SEARCH_INPUT_ID,
  SUPPORT_EMAIL,
} from '../constants';
import {useArticleSearch} from '../hooks/useArticleSearch';
import {useHelpBasePath} from '../hooks/useHelpLocale';

// Zero-result queries are the content backlog — log them once the user
// stops typing.
const ZERO_RESULT_DEBOUNCE_MS = 800;
const ZERO_RESULT_MIN_LENGTH = 3;

const isMacLike = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iP(hone|ad|od)/.test(navigator.platform);

type SearchBarProps = {
  variant?: 'default' | 'hero';
};

const SearchBar = ({variant = 'default'}: SearchBarProps) => {
  const theme = useTheme();
  const {query, setQuery, results} = useArticleSearch();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const {formatMessage} = useIntl();
  const helpBasePath = useHelpBasePath();

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setQuery(value);
      setOpen(value.trim().length > 0);
    },
    [setQuery],
  );

  const handleSelect = useCallback(
    (slug: string) => {
      setQuery('');
      setOpen(false);
      navigate(`${helpBasePath}/${slug}`);
    },
    [helpBasePath, navigate, setQuery],
  );

  const handleBlur = useCallback(() => {
    // Delay closing to allow click on result
    setTimeout(() => setOpen(false), 200);
  }, []);

  const handleFocus = useCallback(() => {
    if (query.trim().length > 0) {
      setOpen(true);
    }
  }, [query]);

  // Log searches that found nothing (debounced until typing settles)
  useEffect(() => {
    const term = query.trim();
    if (term.length < ZERO_RESULT_MIN_LENGTH || results.length > 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      ReactGA.gtag('event', 'help_search_zero_results', {search_term: term});
    }, ZERO_RESULT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, results.length]);

  const isHero = variant === 'hero';
  const hasQuery = query.trim().length > 0;
  const contactHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `SmartMixin Help — ${query.trim()}`,
  )}`;

  return (
    <Box
      ref={setAnchorEl}
      sx={{
        width: '100%',
        maxWidth: isHero ? 600 : 500,
        mb: isHero ? 0 : 3,
      }}>
      <TextField
        fullWidth
        placeholder={formatMessage(messages.common.searchArticles)}
        value={query}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        size={isHero ? 'medium' : 'small'}
        slotProps={{
          htmlInput: {id: HELP_SEARCH_INPUT_ID},
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass
                  color={
                    isHero
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary
                  }
                />
              </InputAdornment>
            ),
            endAdornment: isHero ? (
              <InputAdornment position="end">
                <Box
                  component="kbd"
                  sx={{
                    display: {xs: 'none', md: 'inline-flex'},
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontFamily: 'inherit',
                  }}>
                  {isMacLike() ? '⌘K' : 'Ctrl K'}
                </Box>
              </InputAdornment>
            ) : undefined,
          },
        }}
        sx={
          isHero
            ? {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                  borderRadius: 3,
                  boxShadow: theme.shadows[2],
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                  '&:hover fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiInputBase-input': {
                  py: 1.5,
                  fontSize: '1rem',
                },
              }
            : {}
        }
      />
      <Popper
        open={open && hasQuery}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{zIndex: 1300, width: anchorEl?.offsetWidth}}>
        <Paper
          elevation={8}
          sx={{
            mt: 1,
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: 2,
          }}>
          {results.length === 0 ? (
            // Never a dead end: offer direct contact when search finds nothing
            <Box sx={{p: 2.5, textAlign: 'center'}}>
              <Typography
                variant="body2"
                sx={{color: 'text.secondary', mb: 1.5}}>
                {formatMessage(messages.common.noSearchResults)}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EnvelopeSimple />}
                href={contactHref}>
                {formatMessage(messages.common.contactMe)}
              </Button>
            </Box>
          ) : (
            <Box sx={{p: 1.5, borderBottom: 1, borderColor: 'divider'}}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                }}>
                {formatMessage(messages.common.searchResultsFound, {
                  count: results.length,
                })}
              </Typography>
            </Box>
          )}
          <List disablePadding>
            {results.slice(0, 10).map((result) => (
              <ListItemButton
                key={result.id}
                onClick={() => handleSelect(result.slug)}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                      }}>
                      {result.title}
                    </Typography>
                  }
                  secondary={
                    <Chip
                      label={
                        CATEGORY_MESSAGES[result.category]
                          ? formatMessage(CATEGORY_MESSAGES[result.category])
                          : result.category
                      }
                      size="small"
                      sx={{
                        mt: 0.5,
                        height: 20,
                        fontSize: '0.7rem',
                      }}
                    />
                  }
                  slotProps={{secondary: {component: 'div'}}}
                />
                <Box sx={{ml: 1, display: 'flex', color: 'text.secondary'}}>
                  <CaretRight size={24} />
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};

export default SearchBar;
